import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Person, PersonClient, PersonCreateModel, Role, RoleClient, Gender } from '../../utils/apiClient';
import { useAuth } from '../Auth/AuthContext';
import ApiUrl from '../../utils/config';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { DateLocal, DateType } from '../../common/components/DateLocal/DateLocal';

const UpdatePerson = () => {
  const appContext = React.useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const [person, setPerson] = useState<Person | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const personsClient = new PersonClient(ApiUrl, axios);
  const rolesClient = new RoleClient(ApiUrl, axios);

  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    patronymic: '',
    birthDate: new Date().toISOString().split('T')[0],
    city: '',
    gender: Gender.Male as Gender | undefined,
    roleIds: [] as number[]
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Загрузка данных персоны
        const personData = await personsClient.getPerson(parseInt(id!));
        setPerson(personData);

        // Загрузка доступных ролей
        const rolesData = await rolesClient.getRoles();
        setRoles(rolesData);

        // Установка данных формы из данных персоны
        setFormData({
          lastName: personData.lastName,
          firstName: personData.firstName,
          patronymic: personData.patronymic || '',
          birthDate: new Date(personData.birthDate).toISOString().split('T')[0],
          city: personData.city,
          gender: personData.gender,
          roleIds: personData.personRoles?.map(pr => pr.roleId!) || []
        });
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Проверка, имеет ли пользователь права на редактирование этой персоны
  useEffect(() => {
    // Ждем, пока загрузятся и пользователь, и персона
    if (!loading && person && user) {
      // Разрешаем только если пользователь редактирует свой профиль или является администратором
      if (user.id !== person.id && !isAdmin()) {
        navigate(`/persons/${id}`);
      }
    }
  }, [loading, person, user, id, navigate, isAdmin]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'gender' ? (value || undefined) : value,
    });
  };


  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const roleId = parseInt(e.target.value);
    const isChecked = e.target.checked;

    // Разрешаем только администратору ставить/снимать роль администратора
    const isAdminRole = roles.find(r => r.id === roleId)?.name === 'Админ';

    if (isAdminRole && !isAdmin()) {
      return;
    }

    if (isChecked) {
      setFormData({
        ...formData,
        roleIds: [...formData.roleIds, roleId]
      });
    } else {
      setFormData({
        ...formData,
        roleIds: formData.roleIds.filter(id => id !== roleId)
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      await personsClient.updatePerson(parseInt(id!), new PersonCreateModel({
        lastName: formData.lastName,
        firstName: formData.firstName,
        patronymic: formData.patronymic,
        birthDate: new Date(formData.birthDate),
        city: formData.city,
        gender: formData.gender,
        roleIds: formData.roleIds
      }));

      setSuccess(true);

      // Перенаправление после короткой задержки
      setTimeout(() => {
        navigate(`/persons/${id}`);
      }, 1500);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    }
  };

  if (loading) {
    return <div>Загрузка данных...</div>;
  }

  if (!person) {
    return <div>Пользователь не найден</div>;
  }

  return (
    <div className="person-edit">
      <h2>Редактирование профиля</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">Данные успешно сохранены</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Фамилия:</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Имя:</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Отчество (необязательно):</label>
          <input
            type="text"
            name="patronymic"
            value={formData.patronymic}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label>Дата рождения:</label>
          <DateLocal
            type={DateType.date}
            value={formData.birthDate ? new Date(formData.birthDate) : undefined}
            onChange={(date) => date && setFormData({ ...formData, birthDate: date.toISOString().split('T')[0] })}
            width={200}
            isClearable={false}
          />
        </div>

        <div className="form-group">
          <label>Пол:</label>
          <select
            name="gender"
            value={formData.gender ?? ''}
            onChange={handleInputChange}
          >
            <option value="">Не указан</option>
            <option value={Gender.Male}>Мальчик</option>
            <option value={Gender.Female}>Девочка</option>
          </select>
        </div>

        <div className="form-group">
          <label>Город:</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Роли:</label>
          <div className="checkbox-group">
            {roles.map(role => {
              // Показываем роль администратора только администраторам
              if (role.name === 'Админ' && !isAdmin()) {
                return null;
              }

              return (
                <div key={role.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    id={`role-${role.id}`}
                    value={role.id}
                    checked={formData.roleIds.includes(role.id!)}
                    onChange={handleRoleChange}
                    disabled={role.name === 'Админ' && !isAdmin()}
                  />
                  <label htmlFor={`role-${role.id}`}>{role.name}</label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn">Сохранить</button>
          <button type="button" className="btn btn--back" onClick={() => navigate(`/persons/${id}`)}>Отмена</button>
        </div>
      </form>
    </div>
  );
};

export default UpdatePerson;